import React, { useState } from "react";
import { useTable, List, EditButton, DeleteButton, ShowButton, CreateButton } from "@refinedev/antd";
import { Table, Space, Tag, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useList } from "@refinedev/core";
import { PURCHASE_REQUEST_LIST_QUERY, DELETE_PURCHASE_REQUEST_MUTATION } from "./queries";
import { TEAM_LIST_QUERY } from "../teams/queries";
import { UserInfo } from "../../components/UserInfo";

interface IPurchaseRequestRow {
  id: string;
  requestNo: string;
  teamId: string;
  dateRequested: string;
  dateRequired: string;
  totalAmount: number;
  status: string;
  isUnplanned?: boolean;
  assignedTo?: string;
  assignedBy?: string;
  assignedOn?: string;
  createdAt: string;
  updatedAt: string;
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const colorMap: Record<string, string> = {
    DRAFT: "default",
    SUBMITTED: "processing",
    VALIDATED: "purple",
    PARTIALLY_APPROVED: "warning",
    APPROVED: "success",
    REJECTED: "error",
  };
  return <Tag color={colorMap[status] || "default"}>{status}</Tag>;
};

export const PurchaseRequestList: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");

  // Teams for mapping teamId -> team name
  const { data: teamsData, isLoading: teamsLoading, isError: teamsError } = useList<any>({
    resource: "teams",
    meta: { gqlQuery: TEAM_LIST_QUERY },
  });
  const teams =
    Array.isArray(teamsData?.data)
      ? teamsData?.data
      : (teamsData?.data as any)?.teams?.nodes || [];

  const { tableProps } = useTable<IPurchaseRequestRow>({
    resource: "purchaseRequests",
    initialSorter: [{ field: "createdAt", order: "desc" }],
    initialPageSize: 10,
    meta: {
      gqlQuery: PURCHASE_REQUEST_LIST_QUERY,
    },
  });

  const dataSource: IPurchaseRequestRow[] = ((tableProps as any)?.dataSource || []).filter(
    (row: IPurchaseRequestRow) =>
      !searchTerm || row.requestNo?.toLowerCase?.().includes(searchTerm.toLowerCase())
  );

  return (
    <List
      headerProps={{
        extra: (
          <Space>
            <Input
              placeholder="Search by Request No..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onPressEnter={(e) => setSearchTerm(e.currentTarget.value)}
              style={{ width: 240 }}
            />
            <CreateButton />
          </Space>
        ),
      }}
    >
      <Table {...tableProps} dataSource={dataSource} rowKey="id">
        <Table.Column dataIndex="requestNo" title="Request No" sorter />
        <Table.Column
          dataIndex="teamId"
          title="Team"
          sorter
          render={(teamId: string) => {
            if (teamsLoading) return "Loading...";
            if (teamsError) return "Error";
            const team = (teams as any[]).find((t) => t.id === teamId);
            return team ? team.name : "Unknown";
          }}
        />
        <Table.Column dataIndex="dateRequested" title="Date Requested" />
        <Table.Column
          dataIndex="totalAmount"
          title="Total Amount"
          sorter
          render={(v: number, record: IPurchaseRequestRow) => (
            <span>
              ETB {Number(v ?? 0).toLocaleString()}
              {record.isUnplanned && <Tag color="orange" style={{ marginLeft: 8 }}>Unplanned</Tag>}
            </span>
          )}
        />
        <Table.Column
          dataIndex="status"
          title="Status"
          render={(status: string) => <StatusBadge status={status} />}
          sorter
        />
        <Table.Column
          dataIndex="assignedTo"
          title="Assigned To"
          render={(assignedTo: string) =>
            assignedTo ? <UserInfo userId={assignedTo} /> : <Tag>Unassigned</Tag>
          }
        />
        <Table.Column
          title="Actions"
          dataIndex="actions"
          render={(_text: string, record: IPurchaseRequestRow) => (
            <Space>
              {record.status === 'DRAFT' && (
                <>
                  <EditButton size="small" recordItemId={record.id} resource="purchaseRequests" />
                  <DeleteButton size="small" recordItemId={record.id} resource="purchaseRequests" meta={{ gqlMutation: DELETE_PURCHASE_REQUEST_MUTATION }} />
                </>
              )}
              <ShowButton size="small" recordItemId={record.id} resource="purchaseRequests" />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};